import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { MarketMatch, MarketsResponse, WinnerSelection } from '@brochure/shared'
import { hasSelection, type DraftSlip } from '@/lib/slip'
import { canLoadMore, groupMarkets } from '@/lib/stockbet'
import { phaseKey, phaseParams } from '@/lib/stoccopong-format'
import { MarketCard } from './MarketCard'

type MarketsTabProps = {
  tournamentId: string
  data: MarketsResponse
  slip: DraftSlip
  onToggle: (market: MarketMatch, selection: WinnerSelection) => void
  onLoadMore: () => void
  /** La pagina successiva è in arrivo: il bottone si disattiva, le partite già mostrate restano. */
  isLoadingMore: boolean
  /** L'ultima lettura è fallita: le partite restano, sotto il bottone un avviso; il bottone riprova. */
  hasLoadError: boolean
}

const pickedIn = (slip: DraftSlip, matchId: string): WinnerSelection | null =>
  hasSelection(slip, matchId, { market: 'winner', selection: 'home' }) ? 'home' : hasSelection(slip, matchId, { market: 'winner', selection: 'away' }) ? 'away' : null

/**
 * Mercati: le partite in corso in cima, poi le prossime per fase (le concluse non ci sono). Toccare una quota la mette
 * in schedina. Il palinsesto arriva a pagine: "Carica altro" finché ne restano.
 */
export function MarketsTab({ tournamentId, data, slip, onToggle, onLoadMore, isLoadingMore, hasLoadError }: MarketsTabProps) {
  const { t } = useTranslation()
  const sections = groupMarkets(data.matches)

  const cards = (matches: MarketMatch[], showPhase = true) => (
    <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
      {matches.map((market) => (
        <MarketCard
          key={market.matchId}
          tournamentId={tournamentId}
          market={market}
          picked={pickedIn(slip, market.matchId)}
          onPick={(selection) => data.guest && onToggle(market, selection)}
          showPhase={showPhase}
        />
      ))}
    </div>
  )

  if (data.matches.length === 0) return <p className="font-body text-paper/60">{t('stockbet.markets.empty')}</p>

  return (
    <div className="space-y-9">
      {sections.live.length > 0 && <Section title={t('stockbet.markets.live')}>{cards(sections.live)}</Section>}
      {sections.scheduled.map((group) => (
        <Section key={group.phase} title={t(phaseKey(group.phase), phaseParams(group.phase))}>
          {cards(group.matches, false)}
        </Section>
      ))}
      {canLoadMore(data.matches.length, data.total) && (
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            data-testid="sb-load-more"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="flex min-h-11 items-center rounded-full border border-paper/30 px-6 font-body text-sm font-semibold text-paper transition-colors hover:bg-paper/10 active:bg-paper/15 disabled:opacity-50"
          >
            {t('stockbet.markets.loadMore')}
          </button>
          {hasLoadError && (
            <p role="status" data-testid="sb-load-more-error" className="text-center font-body text-sm text-coral">
              {t('stockbet.markets.loadMoreError')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-xl font-medium tracking-tight text-paper">{title}</h2>
      {children}
    </section>
  )
}
